import { useMemo } from 'react';
import { useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';

import { MeWithPurchasedItems } from '../../../../../../../../graphql/queries/account';
import { HierarchyItemForTypeCheck } from '../../../../../../../../graphql/queries/hierarchy-item';
import { PURCHASED_PRINT_PACKAGE_STATUS } from '../../../../../../../../constants/print-packages';
import { AlbumFolderWithPurchasableItems } from '../../../../../../../../graphql/queries/album-folder';
import { getAlbumMembershipGroupQuery } from '../../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getAlbumMembershipGroup';
import {
  getPurchasedCustomPrintsMissingConfiguration,
  getPurchasedPhotoPricingsMissingConfiguration,
  getPurchasedPrintPackagesMissingConfiguration,
} from '../helpers';
import { AlbumForWebGalleriesPurchases } from '../../../graphql/queries/album';
import { PURCHASED_PHOTO_PRICING_STATUS } from '../../../../../../../../constants/photo-pricings';
import { PURCHASED_CUSTOM_PRINT_STATUS } from '../../../../../../../../constants/custom-prints';

const usePurchasesPendingConfiguration = ({
  hierarchyItemId,
  shippableOnly,
}) => {
  const cart = useSelector(({ checkout }) => checkout.cart);
  const { data: { hierarchyItem } = {} } = useQuery(HierarchyItemForTypeCheck, {
    variables: {
      id: hierarchyItemId,
    },
    skip: !hierarchyItemId,
    fetchPolicy: 'cache-first',
  });

  const albumFolderId =
    hierarchyItem?.album?.root_album_folder?.id ??
    hierarchyItem?.album_folder?.root?.id;

  const { data: { me } = {} } = useQuery(MeWithPurchasedItems, {
    variables: {
      is_prepaid: true,
      album_folder_id: albumFolderId,
      print_packages_status: PURCHASED_PRINT_PACKAGE_STATUS.PENDING,
      photo_pricings_status: PURCHASED_PHOTO_PRICING_STATUS.PENDING,
      custom_prints_statuses: [PURCHASED_CUSTOM_PRINT_STATUS.CUSTOMER_INPUT],
    },
    skip: !albumFolderId,
  });

  const { data: { me: meWithPurchasedItems } = {} } = useQuery(
    MeWithPurchasedItems,
    {
      variables: {
        album_folder_id: albumFolderId,
      },
      skip: !albumFolderId,
    },
  );

  const prepaidPrintPackages = useMemo(
    () => me?.purchased_print_packages?.records ?? [],
    [me],
  );
  const prepaidPhotoPricings = useMemo(
    () =>
      (me?.purchased_photo_pricings?.records ?? []).filter(
        ({
          photo_pricing_print_product_item: printProductItem,
          photo_pricing_print_item: printItem,
        }) => {
          if (!shippableOnly) {
            return true;
          }

          return !!(printProductItem ?? printItem);
        },
      ),
    [me, shippableOnly],
  );
  const prepaidCustomPrints = useMemo(
    () => me?.purchased_custom_prints?.records ?? [],
    [me],
  );

  const hasPrepaidItems = useMemo(
    () =>
      (prepaidPrintPackages ?? []).length > 0 ||
      (prepaidPhotoPricings ?? []).length > 0 ||
      (prepaidCustomPrints ?? []).length > 0,
    [prepaidPrintPackages, prepaidPhotoPricings, prepaidCustomPrints],
  );

  const { data: { albumMembershipGroup } = {} } = useQuery(
    getAlbumMembershipGroupQuery,
    {
      variables: {
        id: hierarchyItemId,
      },
      skip: !hierarchyItemId || !hasPrepaidItems,
      fetchPolicy: 'cache-first',
    },
  );

  const { data: { album } = {} } = useQuery(AlbumForWebGalleriesPurchases, {
    variables: {
      id: hierarchyItemId,
    },
    skip: !hierarchyItemId || !hasPrepaidItems,
  });

  const { data: { albumFolder } = {} } = useQuery(
    AlbumFolderWithPurchasableItems,
    {
      variables: {
        id: albumFolderId,
      },
      skip: !albumFolderId || !hasPrepaidItems,
    },
  );

  const albumPrintPackages =
    albumFolder?.album_folder_print_packages?.records ?? [];
  const photoPricing = albumFolder?.photo_pricing;

  const hasPurchasedPrintPackages =
    (me?.purchased_print_packages?.total ?? 0) > 0 ||
    (meWithPurchasedItems?.purchased_print_packages?.total ?? 0) > 0;

  const albumMembershipGroupEntity = albumMembershipGroup ?? {
    id: album?.id ?? albumFolder?.id,
    album,
    is_album: !!album,
    album_folder: album ? undefined : albumFolder,
  };

  const isAllPhotos = !albumMembershipGroup && !!album;

  return {
    isAllPhotos,
    hierarchyItem,
    albumMembershipGroup: albumMembershipGroupEntity,
    albumPrintPackages,
    photoPricing,
    prepaidPrintPackages,
    prepaidPhotoPricings,
    prepaidCustomPrints,
    hasPurchasedPrintPackages,
    prepaidPrintPackagesMissingConfiguration: getPurchasedPrintPackagesMissingConfiguration(
      cart,
      prepaidPrintPackages,
    ),
    prepaidPhotoPricingsMissingConfiguration: getPurchasedPhotoPricingsMissingConfiguration(
      cart,
      prepaidPhotoPricings,
    ),
    prepaidCustomPrintsMissingConfiguration: getPurchasedCustomPrintsMissingConfiguration(
      cart,
      prepaidCustomPrints,
    ),
  };
};

export default usePurchasesPendingConfiguration;
